import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../../firebase';
import { collection, getDocs, query, orderBy, limit, where } from 'firebase/firestore';
import { useAuth } from '../../context/AuthContext';
import Layout from '../../components/ui/Layout';
import StatCard from '../../components/ui/StatCard';
import Card from '../../components/ui/Card';
import StatusPill from '../../components/ui/StatusPill';
import EmptyState from '../../components/ui/EmptyState';
import LoadingSkeleton from '../../components/ui/LoadingSkeleton';
import { useTranslation } from '../../i18n/LanguageContext';
import {
  IconTractor,
  IconClipboardList,
  IconAlertTriangle,
  IconDroplet,
  IconArrowRight,
  IconTool,
  IconUsers,
  IconClock,
  IconShieldCheck,
} from '@tabler/icons-react';

export default function SiteDashboard() {
  const { t, language } = useTranslation();
  const { userProfile, org, orgId } = useAuth();
  const navigate = useNavigate();
  const [machines, setMachines] = useState([]);
  const [checks, setChecks] = useState([]);
  const [incidents, setIncidents] = useState([]);
  const [defects, setDefects] = useState([]);
  const [staff, setStaff] = useState([]);
  const [tanks, setTanks] = useState([]);
  const [loading, setLoading] = useState(true);

  const siteId = userProfile?.siteId;
  const brandColour = org?.brandColour || 'var(--color-brand)';

  useEffect(() => {
    const load = async () => {
      if (!orgId) return;
      try {
        const [
          machinesSnap,
          incidentsSnap,
          defectsSnap,
          staffSnap,
          tanksSnap,
        ] = await Promise.all([
          getDocs(collection(db, 'organisations', orgId, 'machines')),
          getDocs(query(
            collection(db, 'organisations', orgId, 'incidents'),
            orderBy('detectedAt', 'desc'),
            limit(5)
          )),
          getDocs(query(
            collection(db, 'organisations', orgId, 'defects'),
            orderBy('reportedAt', 'desc'),
            limit(5)
          )),
          getDocs(collection(db, 'organisations', orgId, 'users')),
          getDocs(collection(db, 'organisations', orgId, 'dervTanks')),
        ]);

        const allMachines = machinesSnap.docs.map(d => ({ id: d.id, ...d.data() }));
        const siteMachines = siteId ? allMachines.filter(m => m.siteId === siteId) : allMachines;

        const allStaff = staffSnap.docs.map(d => ({ id: d.id, ...d.data() }));
        const siteStaff = siteId ? allStaff.filter(u => u.siteId === siteId && u.role === 'operator') : allStaff.filter(u => u.role === 'operator');

        const allTanks = tanksSnap.docs.map(d => ({ id: d.id, ...d.data() }));
        const siteTanks = siteId ? allTanks.filter(t => t.siteId === siteId) : allTanks;

        const allIncidents = incidentsSnap.docs.map(d => ({ id: d.id, ...d.data() }));
        const siteIncidents = siteId ? allIncidents.filter(i => i.siteId === siteId) : allIncidents;

        const allDefects = defectsSnap.docs.map(d => ({ id: d.id, ...d.data() }));
        const siteDefects = siteId ? allDefects.filter(d => d.siteId === siteId) : allDefects;

        setMachines(siteMachines);
        setIncidents(siteIncidents);
        setDefects(siteDefects);
        setStaff(siteStaff);
        setTanks(siteTanks);
      } catch (err) {
        console.error(err);
      }
      setLoading(false);
    };
    load();
  }, [orgId, siteId]);

  const hour = new Date().getHours();
  const greeting = hour < 12 ? t('dashboard.greeting_morning') : hour < 17 ? t('dashboard.greeting_afternoon') : t('dashboard.greeting_evening');
  const dateStr = new Date().toLocaleDateString(language, {
    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric'
  });

  const totalMachines = machines.length;
  const blockedMachines = machines.filter(m => m.isBlocked).length;
  const openIncidents = incidents.filter(i => i.status === 'open').length;
  const openDefects = defects.filter(d => d.status === 'open').length;

  const lowestTank = tanks.reduce((min, t) => {
    if (!t.capacityLitres) return min;
    const pct = Math.round((t.currentLitres / t.capacityLitres) * 100);
    return pct < min ? pct : min;
  }, 100);

  const formatTime = (ts) => {
    if (!ts) return '—';
    const date = ts.toDate ? ts.toDate() : new Date(ts);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    if (date.toDateString() === today.toDateString()) {
      return `Today ${date.toLocaleTimeString(language, { hour: '2-digit', minute: '2-digit' })}`;
    }
    if (date.toDateString() === yesterday.toDateString()) {
      return `Yesterday ${date.toLocaleTimeString(language, { hour: '2-digit', minute: '2-digit' })}`;
    }
    return date.toLocaleDateString(language, { day: 'numeric', month: 'short' });
  };

  return (
    <Layout>
      <div style={{ padding: 'var(--main-padding)', minHeight: '100vh' }}>

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 28 }}>
          <div>
            <h1 style={{ fontSize: 22, fontWeight: 400, marginBottom: 4 }}>
              {greeting} {userProfile?.name?.split(' ')[0] || t('dashboard.there')}
            </h1>
            <p style={{ fontSize: 13, color: 'var(--color-text-tertiary)', marginBottom: 8 }}>{dateStr}</p>
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 5,
              fontSize: 11,
              background: brandColour + '18',
              color: brandColour,
              borderRadius: 'var(--radius-full)',
              padding: '3px 10px',
            }}>
              <IconShieldCheck size={11} stroke={1.5} />
              {org?.name || ''}{userProfile?.siteName ? ` · ${userProfile.siteName}` : ''}
            </div>
          </div>
          <button
            onClick={() => navigate('/machines/new')}
            style={{
              background: 'var(--color-brand)',
              color: '#fff',
              border: 'none',
              borderRadius: 'var(--radius-md)',
              padding: '8px 16px',
              fontSize: 13,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 6,
            }}
          >
            <IconTractor size={14} stroke={1.5} /> {t('machines.add')}
          </button>
        </div>

        {/* Stats */}
        {loading ? (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 12, marginBottom: 20 }}>
            {[1,2,3,4].map(i => (
              <div key={i} style={{ background: 'var(--color-bg-primary)', border: '0.5px solid var(--color-border-light)', borderRadius: 'var(--radius-lg)', padding: '18px 20px', height: 88 }} />
            ))}
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 12, marginBottom: 20 }}>
            <StatCard
              value={totalMachines}
              label={t('dashboard.total_machines')}
              colour="var(--color-text-primary)"
              icon={<IconTractor size={16} color="var(--color-text-secondary)" stroke={1.5} />}
              onClick={() => navigate('/machines')}
            />
            <StatCard
              value={blockedMachines}
              label={t('dashboard.blocked_machines')}
              colour={blockedMachines > 0 ? 'var(--color-danger)' : 'var(--color-success)'}
              icon={<IconTractor size={16} color={blockedMachines > 0 ? 'var(--color-danger)' : 'var(--color-success)'} stroke={1.5} />}
              onClick={() => navigate('/machines')}
            />
            <StatCard
              value={openIncidents}
              label={t('dashboard.open_incidents')}
              colour={openIncidents > 0 ? 'var(--color-danger)' : 'var(--color-success)'}
              icon={<IconAlertTriangle size={16} color={openIncidents > 0 ? 'var(--color-danger)' : 'var(--color-success)'} stroke={1.5} />}
              onClick={() => navigate('/incidents')}
            />
            <StatCard
              value={openDefects}
              label={t('dashboard.open_defects')}
              colour={openDefects > 0 ? 'var(--color-warning)' : 'var(--color-success)'}
              icon={<IconTool size={16} color={openDefects > 0 ? 'var(--color-warning)' : 'var(--color-success)'} stroke={1.5} />}
              onClick={() => navigate('/defects')}
            />
          </div>
        )}

        {/* Derv tanks */}
        {tanks.length > 0 && (
          <div style={{ marginBottom: 20 }}>
            <div style={{ fontSize: 14, fontWeight: 400, marginBottom: 12 }}>{t('dashboard.fuel_tanks')}</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 10 }}>
              {tanks.map(tank => {
                const pct = tank.capacityLitres ? Math.round((tank.currentLitres / tank.capacityLitres) * 100) : 0;
                const colour = pct <= 15 ? 'var(--color-danger)' : pct <= 30 ? 'var(--color-warning)' : 'var(--color-success)';
                return (
                  <Card key={tank.id} onClick={() => navigate(`/fuel/${tank.id}`)} hover>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <IconDroplet size={14} color={colour} stroke={1.5} />
                        <span style={{ fontSize: 13, fontWeight: 400 }}>{tank.name}</span>
                      </div>
                      <span style={{ fontSize: 13, fontWeight: 400, color: colour }}>{pct}%</span>
                    </div>
                    <div style={{ height: 3, background: 'var(--color-bg-tertiary)', borderRadius: 2, overflow: 'hidden', marginBottom: 6 }}>
                      <div style={{ height: '100%', width: `${pct}%`, background: colour, borderRadius: 2 }} />
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)' }}>
                      {tank.currentLitres?.toLocaleString() || 0}L {t('common.of')} {tank.capacityLitres?.toLocaleString() || 0}L
                    </div>
                  </Card>
                );
              })}
            </div>
          </div>
        )}

        {/* Machines needing attention */}
        <div style={{ marginBottom: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
            <div style={{ fontSize: 14, fontWeight: 400 }}>{t('dashboard.fleet')}</div>
            <span
              onClick={() => navigate('/machines')}
              style={{ fontSize: 12, color: 'var(--color-brand)', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 3 }}
            >
              View all <IconArrowRight size={13} stroke={1.5} />
            </span>
          </div>
          {loading ? (
            <LoadingSkeleton rows={3} card />
          ) : machines.length === 0 ? (
            <Card>
              <EmptyState
                icon={<IconTractor size={32} stroke={1.5} />}
                title={t('dashboard.no_machines')}
                message={t('dashboard.no_machines_msg')}
                action={
                  <button
                    onClick={() => navigate('/machines/new')}
                    style={{ background: 'var(--color-brand)', color: '#fff', border: 'none', borderRadius: 'var(--radius-md)', padding: '8px 16px', fontSize: 13, cursor: 'pointer' }}
                  >
                    {t('machines.add')}
                  </button>
                }
              />
            </Card>
          ) : (
            <Card padding="0">
              <div style={{ display: 'grid', gridTemplateColumns: '1.8fr 1fr 1fr 0.8fr 80px', gap: 0, padding: '8px 16px', borderBottom: '0.5px solid var(--color-border-light)', background: 'var(--color-bg-secondary)' }}>
                {['machines.col_machine', 'machines.col_serial', 'machines.col_hours', 'machines.col_status', ''].map(h => (
                  <div key={h} style={{ fontSize: 10, color: 'var(--color-text-tertiary)', letterSpacing: '0.04em' }}>{h ? t(h) : ''}</div>
                ))}
              </div>
              {machines.slice(0, 6).map((machine, i) => (
                <div
                  key={machine.id}
                  onClick={() => navigate(`/machines/${machine.id}`)}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '1.8fr 1fr 1fr 0.8fr 80px',
                    gap: 0,
                    padding: '12px 16px',
                    borderBottom: i < Math.min(machines.length, 6) - 1 ? '0.5px solid var(--color-border-light)' : 'none',
                    cursor: 'pointer',
                    background: machine.isBlocked ? 'var(--color-danger-light)' : 'transparent',
                    alignItems: 'center',
                  }}
                  onMouseEnter={e => !machine.isBlocked && (e.currentTarget.style.background = 'var(--color-bg-secondary)')}
                  onMouseLeave={e => !machine.isBlocked && (e.currentTarget.style.background = 'transparent')}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div style={{ width: 28, height: 28, borderRadius: 'var(--radius-md)', background: machine.isBlocked ? 'var(--color-danger-light)' : '#E6F1FB', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <IconTractor size={14} color={machine.isBlocked ? 'var(--color-danger)' : '#185FA5'} stroke={1.5} />
                    </div>
                    <div>
                      <div style={{ fontSize: 13, color: machine.isBlocked ? 'var(--color-danger)' : 'var(--color-text-primary)' }}>
                        {machine.makeLabel} {machine.modelLabel}
                      </div>
                      <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)', textTransform: 'capitalize' }}>{machine.category}</div>
                    </div>
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--color-text-secondary)', fontFamily: 'var(--font-mono)' }}>
                    {machine.registration || machine.serialNumber || '—'}
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>
                    {machine.currentHours ? `${machine.currentHours.toLocaleString()} ${t('common.unit_hrs')}` : '—'}
                  </div>
                  <StatusPill status={machine.isBlocked ? 'blocked' : 'active'} />
                  <div style={{ fontSize: 12, color: 'var(--color-brand)', textAlign: 'right' }}>{t('machines.view')}</div>
                </div>
              ))}
            </Card>
          )}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>

          {/* Recent incidents */}
          <Card padding="0">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 18px', borderBottom: '0.5px solid var(--color-border-light)' }}>
              <div style={{ fontSize: 14, fontWeight: 400 }}>{t('dashboard.recent_incidents')}</div>
              <span onClick={() => navigate('/incidents')} style={{ fontSize: 12, color: 'var(--color-brand)', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 3 }}>
                {t('common.view_all')} <IconArrowRight size={13} stroke={1.5} />
              </span>
            </div>
            {loading ? (
              <LoadingSkeleton rows={3} />
            ) : incidents.length === 0 ? (
              <EmptyState
                icon={<IconAlertTriangle size={24} stroke={1.5} />}
                title={t('incidents.none')}
                message={t('dashboard.no_incidents_msg')}
              />
            ) : incidents.map((inc, i) => (
              <div
                key={inc.id}
                onClick={() => navigate(`/incidents/${inc.id}`)}
                style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '11px 18px', borderBottom: i < incidents.length - 1 ? '0.5px solid var(--color-border-light)' : 'none', cursor: 'pointer' }}
                onMouseEnter={e => e.currentTarget.style.background = 'var(--color-bg-secondary)'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
              >
                <div style={{ width: 8, height: 8, borderRadius: '50%', background: inc.severity === 'high' ? 'var(--color-danger)' : inc.severity === 'medium' ? 'var(--color-warning)' : 'var(--color-text-tertiary)', flexShrink: 0 }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, color: 'var(--color-text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{inc.triggeredByName || t('common.unknown')}</div>
                  <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)' }}>{inc.assetReg || '—'}</div>
                </div>
                <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)', flexShrink: 0 }}>{formatTime(inc.detectedAt)}</div>
              </div>
            ))}
          </Card>

          {/* Recent defects */}
          <Card padding="0">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 18px', borderBottom: '0.5px solid var(--color-border-light)' }}>
              <div style={{ fontSize: 14, fontWeight: 400 }}>{t('dashboard.recent_defects')}</div>
              <span onClick={() => navigate('/defects')} style={{ fontSize: 12, color: 'var(--color-brand)', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 3 }}>
                {t('common.view_all')} <IconArrowRight size={13} stroke={1.5} />
              </span>
            </div>
            {loading ? (
              <LoadingSkeleton rows={3} />
            ) : defects.length === 0 ? (
              <EmptyState
                icon={<IconTool size={24} stroke={1.5} />}
                title={t('dashboard.no_defects')}
                message={t('dashboard.no_defects_msg')}
              />
            ) : defects.map((defect, i) => (
              <div
                key={defect.id}
                onClick={() => navigate(`/defects/${defect.id}`)}
                style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '11px 18px', borderBottom: i < defects.length - 1 ? '0.5px solid var(--color-border-light)' : 'none', cursor: 'pointer' }}
                onMouseEnter={e => e.currentTarget.style.background = 'var(--color-bg-secondary)'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
              >
                <div style={{ width: 8, height: 8, borderRadius: '50%', background: defect.severity === 'critical' ? 'var(--color-danger)' : defect.severity === 'major' ? 'var(--color-warning)' : 'var(--color-info)', flexShrink: 0 }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, color: 'var(--color-text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{defect.description || t('dashboard.defect_reported')}</div>
                  <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)' }}>{defect.reportedBy || '—'}</div>
                </div>
                <StatusPill status={defect.severity || 'minor'} />
              </div>
            ))}
          </Card>

        </div>
      </div>
    </Layout>
  );
}